import { useState } from "react";
import { Check, Copy, TriangleAlert } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useTranslation } from "@/i18n/LanguageContext";

/**
 * One-shot plaintext secret dialog. Shows a value (API key, invite token, etc.)
 * exactly once with a copy button and explicit acknowledgement.
 */
export default function SecretDialog({ open, onClose, title, description, secret, testIdPrefix = "secret" }) {
  const [copied, setCopied] = useState(false);
  const [acknowledged, setAcknowledged] = useState(false);
  const t = useTranslation();

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(secret || "");
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch { /* ignore */ }
  };

  const handleClose = (shouldClose) => {
    if (!shouldClose) return;
    setAcknowledged(false);
    setCopied(false);
    onClose?.();
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="border-border" data-testid={`${testIdPrefix}-dialog`}>
        <DialogHeader>
          <DialogTitle className="font-display tracking-tight">{title}</DialogTitle>
          <DialogDescription className="text-xs">{description}</DialogDescription>
        </DialogHeader>

        <div className="flex items-start gap-2 border-l-2 border-destructive bg-destructive/10 p-3 text-xs text-destructive">
          <TriangleAlert className="mt-[2px] h-4 w-4 shrink-0" strokeWidth={1.5} />
          <span>{t("secretDialog.warning")}</span>
        </div>

        <div className="flex flex-col gap-3 rounded-lg border border-border bg-black p-3 sm:flex-row sm:items-center" data-testid={`${testIdPrefix}-value`}>
          <code className="min-w-0 flex-1 break-all font-mono text-xs text-[#FF8FBF]">{secret}</code>
          <button
            type="button"
            onClick={copy}
            className="inline-flex h-9 items-center justify-center gap-1.5 rounded-lg border border-border px-2 font-mono text-[11px] hover:border-foreground/40"
            data-testid={`${testIdPrefix}-copy-button`}
          >
            {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
            {copied ? t("common.actions.copied") : t("common.actions.copy")}
          </button>
        </div>

        <label className="flex cursor-pointer select-none items-start gap-2 text-xs text-muted-foreground">
          <input
            type="checkbox"
            checked={acknowledged}
            onChange={(e) => setAcknowledged(e.target.checked)}
            className="mt-[2px] accent-foreground"
            data-testid={`${testIdPrefix}-acknowledge-checkbox`}
          />
          {t("secretDialog.acknowledge")}
        </label>

        <DialogFooter>
          <Button
            data-testid={`${testIdPrefix}-close-button`}
            onClick={() => handleClose(true)}
            disabled={!acknowledged}
            className="rounded-lg"
          >
            {t("common.actions.close")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
